/**
 * Character sheets (CSH-001), read from `character_sheet.created` events.
 *
 * After the interview ends, the orchestrator renders one gpt-image-2 reference
 * sheet per cast member — a turnaround plus head studies built from that
 * member's own photos — and announces each with an event whose payload carries
 * the artifact pointer (`sheet_s3_key`, the `cast_s3_key` precedent). This is
 * the read side: which cast member has a sheet, keyed by the same id the cast
 * documents use (`group_id` for people, `cast_id` for dogs).
 *
 * Pure and tolerant: events are producer-facing JSON; anything malformed
 * yields nothing rather than throwing. Later events win per subject, and an
 * event without a key never hides an earlier sheet.
 *
 * The sheet budget (cap 30, #felix-alerts warning above 10 stored) comes from
 * `character_sheets.batch_settled`; Echoes that predate it simply have no
 * budget line.
 */

import type { EchoflicksSessionEvent } from "../types";
import type { SheetDemandStatus } from "./characterSheetDemand";

export type CharacterSheet = {
  subjectKind: "person" | "dog";
  /** person `group_id` or dog `cast_id` — the cast documents' member ids. */
  groupId: string;
  sheetKey: string;
  referenceCount: number | null;
  modelId: string | null;
  occurredAt: string | null;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asText(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

/**
 * Latest sheet per cast member, in event order. Empty when the lane never
 * fired for this echo — the cast simply shows no sheet.
 */
export function characterSheetsByGroup(
  events: EchoflicksSessionEvent[],
): Map<string, CharacterSheet> {
  const sheets = new Map<string, CharacterSheet>();
  const created = (events ?? [])
    .filter((event) => event.event_type === "character_sheet.created")
    .sort((left, right) =>
      String(left.occurred_at ?? "").localeCompare(
        String(right.occurred_at ?? ""),
      ),
    );
  for (const event of created) {
    const payload = asRecord(event.raw_payload);
    if (!payload) continue;
    const groupId = asText(payload.group_id);
    const sheetKey = asText(payload.sheet_s3_key);
    if (!groupId || !sheetKey) continue;
    sheets.set(groupId, {
      subjectKind: payload.subject_kind === "dog" ? "dog" : "person",
      groupId,
      sheetKey,
      referenceCount:
        typeof payload.reference_count === "number"
          ? payload.reference_count
          : null,
      modelId: asText(payload.model_id),
      occurredAt: event.occurred_at ?? null,
    });
  }
  return sheets;
}

// --- Sheet budget (IDS-013 / IDS-017) ----------------------------------------

export const CHARACTER_SHEET_BATCH_EVENT_TYPE =
  "character_sheets.batch_settled";

/**
 * One `character_sheets.batch_settled` event (`character-sheet-batch.v1`): what
 * the sheet lane did for the whole Echo once its last task settled. Counts and
 * ids only — the producer never puts names or filenames here.
 */
export type CharacterSheetBatch = {
  scheduled: number;
  storedPerson: number;
  storedDog: number;
  storedTotal: number;
  skippedByReason: Record<string, number>;
  skippedTotal: number;
  failed: number;
  /** Sheets that finished after the film had already been dispatched. */
  late: number;
  /** `max_sheets_per_echo` (people + dogs). Null when the event omits it. */
  cap: number | null;
  /** `volume_alert_threshold`; null when the alert is off for this release. */
  threshold: number | null;
  alerted: boolean;
  trigger: "settled" | "dispatch_fallback" | string;
  configReleaseId: string | null;
  occurredAt: string | null;
};

function asCount(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? Math.floor(value)
    : null;
}

/**
 * The latest settle for this Echo, or null when the lane predates the tracker
 * (or never ran). Latest, not first: a regroup or the dispatch-time fallback
 * can settle again, and the newer count is the one that describes the film.
 */
export function characterSheetBatchFromEvents(
  events: EchoflicksSessionEvent[],
): CharacterSheetBatch | null {
  const settled = (events ?? [])
    .filter(
      (event) =>
        !!event && event.event_type === CHARACTER_SHEET_BATCH_EVENT_TYPE,
    )
    .sort((left, right) =>
      String(left.occurred_at ?? "").localeCompare(
        String(right.occurred_at ?? ""),
      ),
    );
  const latest = settled[settled.length - 1];
  if (!latest) return null;
  const payload = asRecord(latest.raw_payload) ?? {};
  const storedPerson = asCount(payload.stored_person) ?? 0;
  const storedDog = asCount(payload.stored_dog) ?? 0;
  const skippedByReason: Record<string, number> = {};
  for (const [reason, count] of Object.entries(
    asRecord(payload.skipped_by_reason) ?? {},
  )) {
    const value = asCount(count);
    if (reason && value) skippedByReason[reason] = value;
  }
  return {
    scheduled: asCount(payload.scheduled) ?? 0,
    storedPerson,
    storedDog,
    storedTotal: asCount(payload.stored_total) ?? storedPerson + storedDog,
    skippedByReason,
    skippedTotal: Object.values(skippedByReason).reduce(
      (sum, count) => sum + count,
      0,
    ),
    failed: asCount(payload.failed) ?? 0,
    late: asCount(payload.late) ?? 0,
    cap: asCount(payload.cap),
    threshold: asCount(payload.threshold),
    alerted: payload.alerted === true,
    trigger: asText(payload.trigger) ?? "settled",
    configReleaseId: asText(payload.config_release_id),
    occurredAt: latest.occurred_at ?? null,
  };
}

/**
 * Whether the Echo crossed the sheet-volume alert: the producer says it fired,
 * or the stored count is strictly above the threshold (the alert is claimed
 * once per Echo, so a later settle can carry `alerted: false` for an Echo whose
 * warning already went out). Exactly at the threshold is NOT an alert.
 */
export function sheetVolumeAlerted(batch: CharacterSheetBatch | null): boolean {
  if (!batch) return false;
  if (batch.alerted) return true;
  return batch.threshold !== null && batch.storedTotal > batch.threshold;
}

/** "Alert sent (> 10)" — the amber badge text; null when there is no alert. */
export function sheetVolumeAlertLabel(
  batch: CharacterSheetBatch | null,
): string | null {
  if (!batch || !sheetVolumeAlerted(batch)) return null;
  return batch.threshold !== null
    ? `Alert sent (> ${batch.threshold})`
    : "Alert sent";
}

const humanize = (value: string) => value.replace(/_/g, " ");

/** "12 sheets generated (cap 30)". */
export function sheetBudgetHeadline(batch: CharacterSheetBatch): string {
  const sheets = `${batch.storedTotal} sheet${batch.storedTotal === 1 ? "" : "s"} generated`;
  return batch.cap !== null ? `${sheets} (cap ${batch.cap})` : sheets;
}

/**
 * The detail line under the headline: "10 people + 2 dogs · skipped 3
 * (no clean reference ×2, over cap ×1) · 1 failed · 1 late".
 */
export function sheetBudgetDetails(batch: CharacterSheetBatch): string {
  const parts = [
    `${batch.storedPerson} ${batch.storedPerson === 1 ? "person" : "people"} + ${batch.storedDog} dog${batch.storedDog === 1 ? "" : "s"}`,
  ];
  if (batch.skippedTotal > 0) {
    const reasons = Object.entries(batch.skippedByReason)
      .sort(
        (left, right) => right[1] - left[1] || left[0].localeCompare(right[0]),
      )
      .map(([reason, count]) => `${humanize(reason)} ×${count}`)
      .join(", ");
    parts.push(`skipped ${batch.skippedTotal} (${reasons})`);
  }
  if (batch.failed > 0) parts.push(`${batch.failed} failed`);
  if (batch.late > 0) parts.push(`${batch.late} late`);
  return parts.join(" · ");
}

/** Per cast member: did this person get a sheet, and if not, why. */
export type PersonSheetStatus =
  | SheetDemandStatus
  | { kind: "stored" }
  | { kind: "not_eligible" }
  | { kind: "skipped"; reason: string | null };

type SheetTier = "principal" | "supporting" | "extra" | null;

/**
 * One person's sheet outcome, from what the events can honestly say.
 *
 * - a `character_sheet.created` for the member → stored;
 * - tier `extra` → not eligible (extras never get a sheet);
 * - eligible, batch settled, no sheet → skipped. `batch_settled` carries
 *   counts, not per-person reasons, so a reason is named only when it is the
 *   Echo's ONLY non-stored outcome (one skip reason, or only failures) AND its
 *   count covers every eligible member left without a sheet
 *   (`unsheetedEligible`). One skip across two sheet-less people names nobody:
 *   the other one may simply not have been scheduled.
 *
 * Null when the cast predates ranking (no tier), or when an eligible member's
 * batch has not settled (still generating, or the sheet lane is off for this
 * Echo — the events cannot tell those apart, so nothing is claimed). The card
 * then shows what it always showed: the sheet if there is one.
 */
export function personSheetStatus({
  tier,
  hasSheet,
  batch,
  unsheetedEligible = 1,
}: {
  tier: SheetTier;
  hasSheet: boolean;
  batch: CharacterSheetBatch | null;
  /** Eligible (main/supporting) members of this cast without a sheet. */
  unsheetedEligible?: number;
}): PersonSheetStatus | null {
  if (!tier) return null;
  if (hasSheet) return { kind: "stored" };
  if (tier === "extra") return { kind: "not_eligible" };
  if (!batch) return null;
  const outcomes: Array<[string, number]> = [
    ...Object.entries(batch.skippedByReason),
    ...(batch.failed > 0 ? [["failed", batch.failed] as [string, number]] : []),
  ];
  const [only] = outcomes;
  const attributable =
    outcomes.length === 1 && only[1] >= Math.max(1, unsheetedEligible);
  return { kind: "skipped", reason: attributable ? only[0] : null };
}

/**
 * Every member's status at once, so each skip attribution knows how many
 * eligible people share the Echo's skip counts.
 */
export function personSheetStatuses(
  people: Array<{ groupId: string; tier: SheetTier }>,
  sheetsByGroup: Map<string, CharacterSheet>,
  batch: CharacterSheetBatch | null,
  demand: Map<string, SheetDemandStatus> = new Map(),
): Map<string, PersonSheetStatus | null> {
  const unsheetedEligible = people.filter(
    (person) =>
      (person.tier === "principal" || person.tier === "supporting") &&
      !sheetsByGroup.has(person.groupId),
  ).length;
  return new Map(
    people.map((person) => [
      person.groupId,
      (!sheetsByGroup.has(person.groupId)
        ? demand.get(person.groupId)
        : null) ??
        personSheetStatus({
          tier: person.tier,
          hasSheet: sheetsByGroup.has(person.groupId),
          batch,
          unsheetedEligible,
        }),
    ]),
  );
}

export function personSheetStatusLabel(status: PersonSheetStatus): string {
  switch (status.kind) {
    case "deferred":
      return "Sheet queued for selected cast";
    case "not_selected":
      return "No sheet needed — outside selected cast";
    case "not_needed_original_only":
      return "No sheet needed — original media only";
    case "not_required_by_consumer":
      return "No sheet needed for selected rendering";
    case "demand_unknown":
      return "Sheet need unresolved";
    case "generating":
      return "Generating sheet";
    case "ready":
      return "Sheet ready";
    case "failed":
      return "Sheet unavailable — preparation failed";
    case "outcome_unknown":
      return "Sheet result unconfirmed";
    case "late":
      return "Sheet not ready at reference selection";
    case "stored":
      return "Sheet stored";
    case "not_eligible":
      return "No sheet — not eligible (one-off)";
    case "skipped":
      return status.reason
        ? `Sheet skipped (${humanize(status.reason)})`
        : "No sheet (reason not recorded per person)";
  }
}
